// Forhåndsbesvarte AI-prompts — sparer Claude-kall, gir instant svar,
// og fungerer offline. Skrevet i folkelig, engasjert reiseguide-tone.
// Keys er QUICK_PROMPTS-indeks (0–11).
//
// IKKE inkluder prompts som krever LIVE-data:
// - 11 «Vær i juli-august» → må bruke api/weather for fersk forecast
// - 10 «Budsjett & tips» → har valuta-kurser som kan svinge (CAD/NOK)
// Disse forblir ubesvart så sendQuick faller gjennom til Claude.

export const PREANSWERS = {
  // 0: Topp 5 wow-øyeblikk
  0: `## Fem øyeblikk dere kommer til å snakke om i mange år

1. **Soloppgang ved Moraine Lake (dag 14, ca. 07:15)** — Ten Peaks lyser opp i rosa først, deretter speiles de i det turkis-blå vannet. Få naturopplevelser på jorda er mer fotografert, og det er en grunn til det.

2. **Dawes Glacier i Endicott Arm (dag 7, ca. 05:30)** — Cruiset seiler inn i en fjord 50 km lang, kanter med isfjell, og helt innerst kalver Dawes Glacier rett foran baugen. Gå på dekk tidlig med kaffekoppen.

3. **Mendenhall Glacier på helikopter (dag 7, kl 15:15)** — Allerede booket. Dere lander faktisk på isbreen og går rundt på 200 år gammel is. Det er gåsehud.

4. **Athabasca Falls i Jasper (dag 12)** — En liten foss på papir, men vannmengden + det smale juvet er voldsomt. Lyden er det dere husker.

5. **Whales & Brown Bear Search i Icy Strait (dag 5, kl 14:25)** — Allerede booket. Knølhval-garanti og brunbjørn i Spasski River Valley. Den dyreopplevelsen sommer-Alaska er kjent for.

## Oppfølging
- [Q] Hva er beste tid for stjerneklart over Lake Louise?
- [Q] Kan vi se hval fra dekk uten å bestille tur?
- [Q] Hvor lang gangtur er Athabasca Falls?`,

  // 1: Beste fotomotiver
  1: `## De mest fotogene øyeblikkene på turen

**Vancouver**
- *Stanley Park Seawall* tidlig morgen kl 06:30 — Lions Gate Bridge speiles, lite folk.
- *Granville Island* etter regn — våte brostein, fargene jumper.

**Inside Passage**
- *Skagway-toget* over White Pass — sett dere på *venstre side* av toget på vei opp.
- *Sjøflyet i Juneau* fra lufta over Mendenhall — kjøp dere fly hvis det er klart.

**Rocky Mountains** (alle disse trenger morgenlys 07–10)
- *Moraine Lake* — den klassiske «Tenmotiv-utsikten». Bare shuttle siden 2023.
- *Peyto Lake* — ulve-formet innsjø sett fra Bow Summit. 14 min gange opp.
- *Lake Louise speilbilde* — vindstille morgen kl 07. Glem ettermiddagen, da blir det vind på vannet.
- *Bow Lake* fra Num-Ti-Jah-loungen — gult tre, blå sjø, hvitt fjell.

**Praktisk**
Ta med ett kamera-batteri ekstra på utfluktene. Det kulde-suger seg fortere enn dere tror på cruise-dekk og i isbre-helikopter.

## Oppfølging
- [Q] Når er det «golden hour» i juli på 52°N?
- [Q] Hvilken side av Icefields Parkway har best lys?
- [Q] Trenger jeg ND-filter til fossene?`,

  // 2: Dyreliv vi kan se
  2: `## Hva dere kan forvente å se

**På cruiset (Inside Passage)**
- *Knølhval* — nesten garantert. Beste tid: dag 5 i Icy Strait (turen er allerede booket), og fra dekk på sea days.
- *Spekkhugger* — heldig hvis. Hold øye med dekkmeldingene.
- *Hvithodet havørn* — overalt. Sitter på toppen av grantrær langs hver havn.
- *Sjøløver og hjelmsele* — på bøyer og holmer.

**Brunbjørn**
- *Spasski River Valley* (Icy Strait, allerede booket) — sannsynligvis. Bjørner kommer for laksen.
- *Banff/Jasper* — mulig fra bilen. Holdes alltid avstand. Hold bjørnespray-en lett tilgjengelig.

**Rocky Mountains**
- *Wapiti og elg* — daglig kost i Jasper. Ofte i selve byen om morgenen.
- *Big-horn sheep* — Icefields Parkway, sør for Saskatchewan Crossing.
- *Hvitsidet hjort* — overalt, ofte langs veien i grålysningen.
- *Coyote* — heldig hvis. Stripete logo på baksiden av halen.

**Marginale, men mulige**
- Bever (i sjøer), murmeldyr (på fjell), ulv (sjelden men har vært observert ved Maligne Lake).

**Tips:** Reduser farten i nasjonalparker. De fleste sightings er sjåfører som så «noe» i øyekroken. Stopp aldri midt i veien — bruk pullouts.

## Oppfølging
- [Q] Når på dagen er bjørn mest aktiv?
- [Q] Hvor kjøper vi bjørnespray?
- [Q] Er det isbjørn i Skagway?`,

  // 3: Isbreer & Inside Passage
  3: `## Inside Passage og Dawes Glacier

**Inside Passage** er en 1500 km lang vannvei mellom Vancouver og Skagway, gjemt mellom mainland-BC og en kjede øyer. Dere går aldri mer enn noen kilometer fra land. Det er som å seile gjennom en fjord uten ende.

**Det dere bør se etter fra dekk:**
- *Knølhval-blåst* — vinkelrett vannstråle, ofte sett på sea days.
- *Hvithodet havørn* — i øvre del av grantrær.
- *Vannfall* fra fjellsidene — særlig etter regn.
- *Sel og sjøløve* på bøyer og holmer.

**Dawes Glacier i Endicott Arm (dag 7, ca. 05:30):**
Cruiset svinger inn i Endicott Arm i grålysningen. Fjorden snor seg innover i ca. 1,5 time mens dere ser flere og flere isfjell flyte rundt — blå, hvit, gjennomsiktig. Innerst venter Dawes Glacier — en isvegg 80 m høy. Skipet stopper, vrir seg rundt, og dere får 30–45 minutter foran isen. Hvis dere er heldige *kalver* den (en isbit faller av) med et brak som høres som torden.

**Hva dere trenger:**
- *Varmt tøy* — det er 8–10 °C på dekk, og vinden gjør det kaldere.
- *Solbriller* — isen kaster sterkt lys.
- *Kaffe* fra Cafe al Bacio kl 05:30 — åpent.

**Lite trick:** Gå på babord (venstre) side av dekk 14 om dere vil ha utsikt mens dere snur. Stjørbord blir mot fjellveggen.

## Oppfølging
- [Q] Når er Sea Pass-info om Endicott Arm?
- [Q] Trenger vi solkrem på dekk?
- [Q] Kan vi spise frokost utendørs den dagen?`,

  // 4: Beste på skipet
  4: `## Topp 5 om bord på Celebrity Solstice

1. **Persian Garden Spa** (inkludert i AquaClass!)
   Heated tile beds, dampsauna med eukalyptus, hammam. Bruk det hver eneste sea day. Tomt om morgenen.

2. **Blu Restaurant** (kun for AquaClass)
   Det er årsaken AquaClass koster det den koster. Bedre service og mindre travelt enn hovedrestauranten. Bestill bord kl 19:30, ikke 18 (mindre cruise-gjenger).

3. **Lawn Club på dekk 15**
   Ekte gress (!) på et cruise-skip. Bocce, golf-putting, og lyser opp om kvelden. Bestill «Lawn Club Grill» en kveld — middag med grill-mester, $$.

4. **Sunset Bar (helt akter, dekk 14)**
   Den beste plassen for å se Inside Passage forsvinne bak skipet. Bestill en gammeldags og se på fugler. Ingen kø.

5. **Captain's Welcome i Theater (dag 2, kl 21)**
   Litt klisjé, men kapteinen forteller om ruta, isbreer og hva dere kan forvente. Drink i hånda, ingen forpliktelser.

**Andre småskatter:**
- *Aqua Spa Cafe* (dekk 14) — fersk juice + sunne tapas dere ikke ser i hovedbufféen.
- *Cellar Masters wine bar* (dekk 4) — flight-smaking, dyrt men gøy én kveld.
- *World Class Bar* — cocktails fra Speed Rack-finalister.

**Hva dere kan droppe:** Casino (samme overalt), kjøpsdager («$10 alligator»-kjøkken-show), og art auction.

## Oppfølging
- [Q] Trenger vi å reservere Persian Garden?
- [Q] Hvor mye koster Lawn Club Grill?
- [Q] Hva er WiFi-koden om bord?`,

  // 5: Landutflukter Alaska
  5: `## Rangerte landutflukter (Skagway, Juneau, Ketchikan)

### Allerede booket
- **Dag 5 Icy Strait** — Whales & Brown Bear Search (5t, kr 5 749, start 14:25). Catamaran for hvalsafari + buss til Spasski Valley for brunbjørn. Garantert det dere kommer til å snakke om.
- **Dag 6 Skagway** — Goldrush Sled Dogs & White Pass Train (5t, kr 3 349, start 07:30). Tog + sledehunder. Husk pass — toget krysser inn i Canada.
- **Dag 7 Juneau** — Mendenhall Glacier by Helicopter & Walk (2t 15m, kr 6 115, start 15:15). Helikopter + isvandring på selve breen. Maks 113 kg per gjest, ingen GoPro-stenger.

### Dag 8 Ketchikan — ingen booket
*Mine anbefalinger:*

**Hvis vær er fint:**
- **Misty Fjords Floatplane** (2t, ~$320/p) — flytur inn i et 900-meters klippelandskap. Lander på sjø, åpner luken, alt blir helt stille. Best.
- **Saxman Native Village + totempåler** (3t, $80/p) — kulturell, totempåle-snikking, god intro til Tlingit-historie.

**Hvis vær er dårlig:**
- **Bli i byen.** Walk Creek Street (gamle red-light-distrikt på påler), lunsj på Annabelle's Keg & Chowder House (laks!), Totem Heritage Center (50 totempåler innendørs).

**Hva som er overvurdert:** Lumberjack Show (klisjé), Bering Sea Crab Fisherman's Tour (dyr og ofte avlyst pga vær).

**Praktisk:** Skipet er bare i havn 6–7 timer i Ketchikan. Velg én ting + spasertur.

## Oppfølging
- [Q] Hvor mye tips på utflukter?
- [Q] Hva slags klær til helikopter-turen?
- [Q] Kan vi avbestille hvis vær er dårlig?`,

  // 6: Rocky Mountains-ruten
  6: `## Bilturen Kamloops → Calgary

**Total avstand:** ca. 1 100 km over 7 dager. Kjørearbeid: 4–5 timer per dag mellom hotellbyene.

### Dag 9 (26. juli) — Vancouver → Kamloops, 355 km
Hwy 5 «Coquihalla» nord. Bratt og dramatisk fjellpass (1244 m), så ned i tørt halvørken-landskap. Sjokkerende kontrast til kysten.
*Stopp:* Hope for kaffe (Blue Moose), Kamloops Wine Trails om dere har tid.

### Dag 10 (27. juli) — Kamloops → Jasper, 430 km
Hwy 5 nord forbi Clearwater og Wells Gray-parken (utenom ruten, men verdt en avstikker), over Yellowhead Pass.
*Stopp:* Mount Robson Visitor Centre (~50 km før Jasper). Berg Lake Trail første km er flat og familievennlig. Kafé på stedet.

### Dag 11 (28. juli) — Jasper-dag
Ingen kjøring mellom byer. Athabasca Falls, Maligne Lake (1,5t båttur til Spirit Island — bestill dagen før!), eller Whistlers SkyTram (gondol opp til 2277 m).

### Dag 12 (29. juli) — Jasper → Banff via Icefields Parkway, 290 km
*Den mest spektakulære kjørestrekningen i Nord-Amerika.* Beregn 6 timer med stopp. Fyll bensin i Jasper, ingen stasjoner mellom Saskatchewan Crossing og Lake Louise.

**Beste stopp i rekkefølge fra nord:**
- Athabasca Falls (15 min)
- Sunwapta Falls (10 min)
- *Columbia Icefield* (1,5t — Ice Explorer-buss bestilles)
- Saskatchewan Crossing (lunsj-stopp)
- Bow Lake + Num-Ti-Jah Lodge (kaffe)
- *Peyto Lake* (45 min — gå opp til Lower Lookout)

### Dag 13 (30. juli) — Banff/Lake Louise-dag
Hovedmål: Lake Louise + Moraine Lake (kun shuttle siden 2023). Bestill shuttle på parkscanada.gc.ca dagen før. Få opp 07:00 — sjefen er der allerede.

### Dag 14 (31. juli) — Banff lokalt
Banff Gondola (book online), Cave & Basin, Banff Avenue om kvelden.

### Dag 15 (1. aug) — Banff → Calgary, 130 km
Lett kjøring. Stopp i Canmore for lunsj (Communitea, Crazyweed). Calgary-innsjekk samme dag.

**Praktisk gjennomgang:**
- Internasjonalt førerkort: ja, men ikke ofte sjekket.
- Bensinfylling: Costco er billigst hvis dere har medlemskort.
- Parks Canada Annual Pass: lønner seg ved 3+ parker — det er deres tilfelle.
- *Bjørnespray*: kjøp på MEC i Vancouver eller bensin/jernvare i Banff.

## Oppfølging
- [Q] Hvor lenge tar Maligne Lake-båttur?
- [Q] Hva koster Ice Explorer-buss?
- [Q] Trenger vi vinterkjede i juli?`,

  // 7: Fairmont Jasper Lodge
  7: `## Fairmont Jasper Park Lodge

**Hva det er:**
Et 700 mål-stort hotellanlegg ved Beauvert Lake. Ikke ett stort bygg, men cottages og lodges spredd langs sjøen. Bygget av Canadian National Railway i 1922.

**Dere har:** Junior Suite King · Lake View · check-in 16:00, 2 netter (27–29. juli).

### Hva som gjør det spesielt
- **Lokalt dyreliv** — wapiti rusler bokstavelig talt mellom bygningene om morgenen og kvelden. Helt vant til mennesker, men hold avstand (de er store).
- **Beauvert Lake** — gå rundt om morgenen, ca. 30 min. Ofte vindstille kl 07 og speilet er som glass.
- **Stargazing** — Jasper er en *Dark Sky Preserve*. Om dere er heldige med vær: melkeveien fra hotellets brygge er noe annet.
- **Spa Fairmont** — varme bad utendørs etter en dag i fjellet. Bestill 60-min reflexology eller deep tissue.

### Hva dere må gjøre
1. **Frokost på Orso Trattoria** — den italienske kjøkkenet med utsikt over sjøen. Bestill bord på forhånd.
2. **Kanoutleie** ved sjøen (CAD 40/time) — fantastisk på Beauvert.
3. **The Great Hall-bar** — historisk peisrom, drink før middag.

### Praktisk
- **Wapiti-advarsel:** ikke gå mellom mor og kalv, og ikke nærmere enn 30 m.
- **Sykler** kan lånes gratis i resepsjonen — tur til Annette Lake (10 min) er flott.
- **Kveldsmat:** Orso (italiensk), Moose's Nook (kanadisk fine dining — book i forveien), Emerald Lounge (peisrom + tapas).
- **Wi-Fi:** treg i lodgene. Last ned offline-data først.

### Hva som *ikke* er verdt det
- Hotellets golfplass — fin nok, men ikke verdt en hel ettermiddag i Jasper.
- Spa er bra, men ikke verdens beste — bruk det heller mellom andre aktiviteter.

## Oppfølging
- [Q] Trenger vi reservere kano på forhånd?
- [Q] Når er solnedgangen i Jasper 28. juli?
- [Q] Er Orso Trattoria glutenfri-vennlig?`,

  // 8: Icefields Parkway
  8: `## Icefields Parkway — guide fra nord til sør

230 km mellom Jasper og Lake Louise. Bygd som arbeidsprosjekt under depresjonen, åpnet 1940. National Geographic kaller den «én av verdens 10 mest scenic kjøreturer».

**Beregn 6–8 timer med stopp.** Mange vil bare ha 4, og angrer.

### Praktisk før dere starter
- Fyll bensin i Jasper, ingen stasjoner mellom Saskatchewan Crossing og Lake Louise.
- Tak vannflaske + matpakke. Restauranter er sparsomme.
- Parks Canada Pass kreves (har dere).
- Mobildekning forsvinner stort sett. Last ned Maps-offline.

### Stopp fra nord (Jasper) til sør (Lake Louise)

**1. Athabasca Falls (km 30)** — 15 min. 23 m foss i et smalt juv.

**2. Sunwapta Falls (km 55)** — 10 min. Mindre kjent, mindre folk.

**3. Tangle Creek Falls (km 100)** — 5 min, rett ved veien.

**4. Columbia Icefield Discovery Centre (km 105)** — 2–3 timer.
- Ice Explorer Bus ut på Athabasca Glacier (~CAD 100/p, bestill online).
- Skywalk-platform 280 m over Sunwapta Valley (inkludert i bussbillett).
- Kafé + suvenirbutikk på stedet.

**5. Parker Ridge Trail** (km 120) — 2,5 km, ~1 time t/r. Litt klatring, men utsikten over Saskatchewan Glacier er sjelden. Hvis dere har energi.

**6. Saskatchewan Crossing (km 150)** — lunsjpause. The Crossing Resort har kafé.

**7. Mistaya Canyon (km 175)** — 10 min, kort gangtur til en smal juv-bro.

**8. Bow Lake + Num-Ti-Jah Lodge (km 200)** — 30 min. Rød taklagde lodge ved en turkis sjø. Kaffe inne, gå langs nordsiden.

**9. Peyto Lake** (km 207) — 45 min.
Lower viewpoint: 5 min fra parkering (lett).
Upper viewpoint: 15 min til (litt klatring, mye finere). Best lys 10–13.

**10. Crowfoot Glacier viewpoint** (km 215) — 5 min. Tre «kråkeklør» som har trukket seg merkbart tilbake siste 30 år.

### Min anbefalte rekkefølge
Hvis dere har 6 timer, prioritér i denne rekkefølgen:
1. Columbia Icefield (Ice Explorer)
2. Peyto Lake
3. Bow Lake
4. Athabasca Falls
5. Mistaya Canyon

Hopp over Sunwapta, Tangle, Parker Ridge hvis tiden er knapp.

### Beste lys
- *Nordlige delen* (Athabasca til Columbia): morgen, før 11.
- *Sørlige delen* (Bow, Peyto): midt på dagen, 11–14.
- Kveldsfargene mellom Bow og Peyto er magisk hvis dere har en grunn til å overnatte i Lake Louise.

## Oppfølging
- [Q] Hvor lang er Ice Explorer-turen?
- [Q] Kan vi gå på Athabasca Glacier på egen hånd?
- [Q] Trengs det grus-dekk på Parkway?`,

  // 9: Restauranter Banff
  9: `## Restauranter i Banff — rangerte topp 5

### 1. Three Ravens (på The Banff Centre)
*Fine dining med fantastisk fjell-utsikt.* Lokalt kjøtt, kanadiske ingredienser, sesongbasert meny. Reserver vindusbord. Hovedrett ~CAD 50.

### 2. Park Distillery
*Whiskey + steak + livsglede.* Egne brennevin destillert på huset, biff fra Alberta. Mer avslappet enn Three Ravens. Hovedrett CAD 35–45.

### 3. The Bison Restaurant
*Vilt og lokal mat.* Som navnet sier — bisongryte, elk-tartar, anke fra Lake Louise. Drikkemenyen er sterk på sider og craft-beer. Hovedrett CAD 40.

### 4. Block Kitchen & Bar
*Bedre småretter til deling.* Smårette-stil, perfekt om dere ikke er sultne på en hel middag. Asiatisk-fusion. CAD 18–28 per rett, ta 3–4 mellom dere.

### 5. Eddie Burger Bar
*Hvis dere bare vil ha en god burger.* Lokal beef, fjellbjørnost, sjåføradapterte priser. CAD 24.

### Bonus — lunsj og brunch
- **Tooloulou's** — cajun-frokost (krabbe-eggesalat, beignets). Ofte 30 min kø, men det er verdt det.
- **Wild Flour Bakery** — beste kaffe i byen + croissanter. Frokost-stopp før Banff Gondola.
- **The Maple Leaf** — kanadisk klassisk på Banff Avenue. Trygt valg hvis dere ikke gidder å lete.

### Hva dere kan droppe
- *Magpie & Stump* — mange stjerner men ofte støynivå som flyplass og portionene er små for prisen.
- *Earls Banff* — kjede, dere kan ta det hvor som helst.
- *Saltlik Steakhouse* — meget okei, men ikke verdt 100 CAD per hovedrett når andre er bedre.

### Praktisk
- **Tipping:** 15–20 % på regninga, ofte foreslått på maskinen.
- **Reservasjon:** alle top-5 anbefales å bestille 1–2 dager før i juli.
- **Lunsj** er drastisk billigere enn middag samme sted — vurder lunsj på Three Ravens, dinner på Park Distillery.

## Oppfølging
- [Q] Hvor finner vi god kanadisk vin?
- [Q] Hvor langt fra Moose Hotel er Three Ravens?
- [Q] Er det glutenfri-vennlige restauranter?`,

  // 10: Budsjett & tips — fjernet, krever live valuta-kurser
  // 11: Vær — fjernet, krever live forecast

  // (gammelt budsjett-svar holdt som referanse i historikk via git)
  __unused_10: `## Ekstra-kostnader underveis — realistisk estimat

Mest er allerede betalt (155 700 NOK i tilbudet inkluderer fly, cruise, hoteller, bil). Det dere må budsjettere for:

### På cruiset (Celebrity Solstice, 7 netter)
- **Auto-gratuity** (drikkepenger) — *inkludert i AquaClass*. Sjekk Sea Pass.
- **Drikker utenom pakka:** vin/øl/spritglass CAD 10–15. Beregn CAD 50–80/dag for en moderat to-glass-til-middag-rutine = **CAD 350–560**.
- **Bilder fra fotografer:** dyrt og kjedelig. Hopp over.
- **Spa utover Persian Garden:** massasje CAD 200/60min. *Skip det.*
- **Internett:** har AquaClass-pakke (sjekk Sea Pass).
- **Landutflukter** — allerede booket for Icy Strait, Skagway, Juneau (totalt ~15 000 NOK). Ketchikan: planlegg CAD 200–400.

**Subtotal cruise:** CAD 600–1 000 ekstra.

### Restauranter (Vancouver + Rocky Mountains)
Vancouver 17–19. juli: ~3 middager + lunsj/frokost.
- Middag på bra sted: CAD 60–80 per person inkl. drikke.
- Lunsj: CAD 25–35 per person.
**Subtotal Vancouver:** CAD 300–400.

Rocky Mountains 27–31. juli: ~4 middager (en del på hotellet, inkludert frokost på Fairmont B&B).
- Hotellfrokost inkludert på Jasper og Kamloops.
- Middag ute: CAD 60–80 pp.
**Subtotal Rockies-mat:** CAD 400–500.

### Bilkjøring
- **Drivstoff** ca 1 100 km / 8 L/100 km / CAD 1,55/L = **CAD 140**.
- **Enveisgebyr** Vancouver→Calgary er CAD 336 (oppgitt i kontrakten).
- **Parkering** i Banff og Lake Louise: CAD 10–20 per gang. Beregn CAD 50 totalt.
- **Toll/avgifter:** ingen i Canada.

**Subtotal bil:** CAD 530.

### Aktiviteter ikke booket
- *Banff Gondola:* CAD 80/p = **CAD 160**.
- *Maligne Lake Cruise to Spirit Island:* CAD 100/p = **CAD 200**.
- *Lake Louise canoe:* CAD 145/time per kano = **CAD 145**.
- *Columbia Icefield Ice Explorer + Skywalk:* CAD 110/p = **CAD 220**.

**Subtotal aktiviteter:** CAD 725.

### Diverse
- Suvenirer/gaver: CAD 200–400.
- Tips på hoteller (CAD 5/natt per person × 5 hoteller): CAD 50.
- Apotek/tannkrem/akrylkofferter etc: CAD 100.

**Subtotal diverse:** CAD 350–550.

### Total ekstra-budsjett
**CAD 2 800 – CAD 3 700**
≈ NOK 21 500 – NOK 28 300

### Tips-praksis Canada/Alaska
- **Restaurant:** 15–18 % standard, 20 % hvis veldig fornøyd. Vises som forslag på maskinen.
- **Taxi:** 10–15 %.
- **Hotellbagasje-bærer:** CAD 2–3 per bag.
- **Housekeeping:** CAD 3–5 per natt, etterlat på pute.
- **Cruise:** automatisk for AquaClass — ingen ekstra på lugar/restaurant.
- **Landutflukt-guide:** CAD 5–10 per person hvis dere var fornøyde.

### Sparetips
- Bruk *Costco-bensinstasjon* hvis dere har medlemskort (sannsynligvis ikke). Ellers Husky.
- *Parks Canada Annual Pass* (CAD 145/familie) lønner seg ved 3+ parker — dere bruker Jasper + Banff + Lake Louise.
- *Lunsj* på fine restauranter er 30–40 % billigere enn middag samme sted.
- *Tim Hortons* kaffe + bagel = CAD 6 versus Starbucks CAD 12.

## Oppfølging
- [Q] Hvor mye er Parks Canada Pass verdt?
- [Q] Er det billigere med ferdigpakka utflukter?
- [Q] Hvilken valuta lønner seg å ta ut?`,

  __unused_11: `## Værmelding for de tre regionene (17. juli – 3. august)

### Vancouver (17–19. juli)
**Forventet:** 18–24 °C, lite regn, mest sol.
*Juli er den tørreste måneden.* Pakk lette klær, men ta med vindjakke for kvelden ved sjøen (Stanley Park kveld kan være 14 °C).

### Inside Passage / SE-Alaska (19–26. juli)
**Forventet:** 12–16 °C på land, 8–12 °C på dekk pga vind. Regn 50–60 % av tiden — det er regnskog.

*Dette er den dagen folk pakker feil for.* Forventningene fra «Alaska sommer» er at det er varmt. Det er det ikke. Pakk:
- *Vindtett regnjakke* (Gore-Tex eller lignende)
- *Lue og hansker* — særlig for sjøfly-turen i Juneau dag 7 (6–10 °C, vinden suger)
- *Tre lag* — bomulls-t-skjorte, fleece, vind/regn-jakke
- *Vanntette joggesko* eller lette fotturstøvler

### Rocky Mountains (26. juli – 1. august)
**Forventet:** 18–24 °C dagtid, 8–12 °C natt. Tordenvær om ettermiddagen — typisk juli i Rockies.

*Mikroklima er stort.* Kamloops kan være 30 °C ørken-varmt, mens Banff samme dag kan ha 16 °C og regn. Sjekk værmeldinga om morgenen.

**Pakketips:**
- *Solbriller + solkrem* (sterk UV i høyden)
- *Vindjakke* (fjellfoss-områder, blåser ofte 4–6 m/s)
- *Lett dunjakke* eller varm fleece til kvelder
- *Lange bukser* mot mygg (særlig Jasper)

### Calgary (1–3. august)
**Forventet:** 22–27 °C, sol. Tørrere prairie-klima. Lett-klær igjen.

### Værvarsel-detaljer
- Jeg sjekker live-data via NOAA og MET ved hver dagsoppstart. Brief'en på forsiden viser temp + glyf.
- *Nordlys:* sommer-Alaska har for lyse netter for å se nordlyset normalt, men om Kp-indeksen går over 5 (storm), kan dere se det. Brief'en sjekker NOAA Space Weather automatisk.

### Praktisk klesliste
- *Vindjakke:* alltid med dere. Også til Banff.
- *Lett dunjakke:* pakkbar, til kvelder i Rocky Mountains og dekk på cruiset.
- *Joggesko:* dagsbruk overalt.
- *Lett kjole/skjorte* til middag på Blu eller Three Ravens.

Pakkelisten i appen har værvarsel-tips øverst som oppdaterer seg.

## Oppfølging
- [Q] Hvor mye snø er det på Athabasca Glacier i juli?
- [Q] Trengs det myggspray i Jasper?
- [Q] Når kan vi sjekke 10-dagers-værmeldinga?`,
};

export function getPreAnswer(promptIdx){
  return PREANSWERS[promptIdx] || null;
}
